/** @jsx jsx */
import { jsx } from '@emotion/react'
import { css } from '@emotion/react/macro'
import { useState } from 'react'
import { MultiSelectProps, OnInputChangeEventContext } from 'react-crane'
import { Chip } from '@mui/material'

import Dropdown, { DropdownMode } from '../Dropdown'
import FormLabel from '../FormLabel'
import { secondaryTextColor } from '../../../../lib/styles/colors'

interface ChipFilterMultiSelectProps extends MultiSelectProps {
  /** Alternate Label Keys for searching or displaying options, include labelKey if applicable */
  altLabelKeys?: Array<string>
  /** Returns label to be displayed in the chip */
  chipLabel?: (option: any) => string
  /** Id of control */
  id: string
  /** Label for field */
  label: string
  /** Optional callback when an item is removed */
  onDelete?: (eventContext: any) => void
  /** Optional callback when an item is selected */
  onSelect?: (eventContext: any) => void
  /** List of options for the dropdown */
  options: Record<string, unknown>[]
  /** Returns label to be displayed in the render options list */
  optionLabel?: (option: any, selected: boolean) => string
  /** Search options using input value as searchText for server side searching */
  searchOptions?: (searchText: string) => void
  /** Selected options */
  selectedOptions: Record<string, unknown>[]
  /** Sets value of selected options in parent component (Type specified in parent component) */
  setSelectedOptions: (options: any[]) => void
  /** Indicates that label should be screen-ready only */
  srOnlyLabel?: boolean
  /** Number of values labels to render before displaying "# selected" if > 0 */
  valueLabelLimit?: number
}

const inputContainerCss = css`
  position: relative;

  .crane-select-inner-input-flex {
    padding-left: 0;
    max-height: 200px;
    overflow: auto;
  }

  .MuiChip-root {
    height: 24px;
    margin-right: 4px;
    margin-bottom: 4px;

    &.Mui-disabled {
      opacity: 0.8;
    }
  }
`
const selectedOptionCss = css`
  color: ${secondaryTextColor};
`

const defaultProps: Pick<
  ChipFilterMultiSelectProps,
  | 'altLabelKeys'
  | 'chipLabel'
  | 'onDelete'
  | 'onSelect'
  | 'optionLabel'
  | 'searchOptions'
  | 'srOnlyLabel'
  | 'valueLabelLimit'
> = {
  altLabelKeys: undefined,
  chipLabel: undefined,
  onDelete: undefined,
  onSelect: undefined,
  optionLabel: undefined,
  searchOptions: undefined,
  srOnlyLabel: false,
  valueLabelLimit: -1
}

const ChipFilterMultiSelect = ({
  altLabelKeys,
  chipLabel,
  disabled,
  id,
  label,
  onDelete,
  onSelect,
  optionLabel,
  options,
  searchOptions,
  selectedOptions,
  setSelectedOptions,
  valueLabelLimit,
  ...props
}: ChipFilterMultiSelectProps) => {
  const { labelKey, valueKey } = { ...props }

  const [focused, setFocused] = useState(false)
  const [inputValue, setInputValue] = useState('')

  const onInputChange = (e: OnInputChangeEventContext) => {
    setInputValue(e.value)
    if (searchOptions) searchOptions(e.value)
  }

  const onFocus = () => setFocused(true)
  const onBlur = () => setFocused(false)

  const getOptionLabel = (option: any) => {
    if (!labelKey) {
      return ''
    }
    return option[labelKey]
  }

  const matchOption = (option: any) => {
    let searchValue = ''

    // Search entire option by alternative keys
    if (altLabelKeys) {
      altLabelKeys.forEach((el) => {
        searchValue += option[el].toLowerCase()
      })
    } else {
      searchValue = getOptionLabel(option).toLowerCase()
    }

    return searchValue.indexOf(inputValue!.toLowerCase()) > -1
  }

  const filterOptions = () => {
    const searchText = inputValue || ''

    if (searchText === '') {
      return options
    }

    const filteredOptions = options.filter((option: any) => matchOption(option))

    return filteredOptions
  }

  const optionRenderer = ({ option, selected }: any) => (
    <div css={selected && selectedOptionCss}>
      {optionLabel ? optionLabel(option, selected) : option[labelKey!]}
    </div>
  )

  const valueComponent = ({ option }: any) => (
    <Chip
      aria-label={`Chip: ${
        chipLabel ? chipLabel(option) : option[labelKey!]
      }.  Press Delete or Backspace to remove.`}
      key={`chip-${option.id}`}
      className="chipCss"
      disabled={disabled}
      onDelete={() => handleDeleteChip(option)}
      label={chipLabel ? chipLabel(option) : option[labelKey!]}
    />
  )

  const handleDeleteChip = (option: any) => {
    const newOptions = selectedOptions?.filter((el: any) => el[valueKey!] !== option[valueKey!])
    setSelectedOptions(newOptions)
    if (onDelete) {
      onDelete(option)
    }
  }

  const onKeyDown = (eventContext: any) => {
    const deleteChipEvent = eventContext.code === 'Backspace' || eventContext.code === 'Delete'
    if (!inputValue?.length && deleteChipEvent && selectedOptions?.length) {
      handleDeleteChip(selectedOptions[selectedOptions.length - 1])
    }
  }

  const handleSelect = (eventContext: any) => {
    let newOptions = [...selectedOptions]
    if (newOptions) {
      const existingValue = newOptions.find(
        (val) => val[valueKey!] === eventContext.value[valueKey!]
      )
      if (existingValue) {
        newOptions = newOptions.filter((item) => item[valueKey!] !== existingValue[valueKey!])
      } else {
        newOptions.push(eventContext.value)
      }
    } else {
      newOptions = [eventContext.value]
    }
    setSelectedOptions(newOptions)

    if (onSelect) {
      onSelect(eventContext)
    }
  }

  const opts = inputValue && !searchOptions ? filterOptions() : options

  return (
    <div css={inputContainerCss}>
      <FormLabel associatedId={id} focused={focused} shrink={!!selectedOptions?.length || focused}>
        {label}
      </FormLabel>
      <Dropdown
        {...props}
        id={`${id}-dropdown`}
        arrowComponent={() => null}
        delimiter=" "
        disabled={disabled}
        inputId={id}
        inputValue={inputValue}
        isSearchable
        labelKey={labelKey}
        mode={DropdownMode.Multi}
        onBlur={onBlur}
        onFocus={onFocus}
        onInputChange={onInputChange}
        onKeyDown={onKeyDown}
        onSelect={handleSelect}
        optionRenderer={optionRenderer}
        options={opts}
        showInput
        showValuesWhileFocused
        value={selectedOptions}
        valueComponent={valueComponent}
        valueKey={valueKey}
        valueLabelLimit={valueLabelLimit}
      />
    </div>
  )
}

ChipFilterMultiSelect.defaultProps = defaultProps

export default ChipFilterMultiSelect
